import { BankType } from "@api/graphql";
import { ValidationErrors } from "@pages/register/hooks/errorResolver";
import { UserDetailsInput } from "@pages/register/steps/RegisterFormTypes";
import { BSB_REGEX, NO_ASTERISK_REGEX } from "@util/regex";

export const bankDetailsErrorResolver = (
  data: UserDetailsInput,
  bank_type: BankType,
  participantTypes?: string[],
): { values: UserDetailsInput; errors: ValidationErrors<UserDetailsInput> } => {
  const errors: ValidationErrors<UserDetailsInput> = {};

  // All bank details fields are optional for Artist/Beneficiary
  const isArtistOrBeneficiary = participantTypes?.some((type) => ["ARTIST", "BENEFICIARY"].includes(type));
  if (isArtistOrBeneficiary) {
    return { values: data, errors };
  }

  if (bank_type === BankType.DOMESTIC) {
    const { account_name, bsb, account_number } = data.bank_details;
    const hasDomesticFieldsFilled = !!(account_name || bsb || account_number);

    if (hasDomesticFieldsFilled) {
      if (!account_name) {
        errors.bank_details = {
          ...errors.bank_details,
          account_name: { type: "required", message: "Account name is required" },
        };
      } else if (!NO_ASTERISK_REGEX.test(account_name)) {
        errors.bank_details = {
          ...errors.bank_details,
          account_name: { type: "pattern", message: "Account name is not valid" },
        };
      }

      if (!bsb) {
        errors.bank_details = {
          ...errors.bank_details,
          bsb: { type: "required", message: "BSB is required" },
        };
      } else if (!NO_ASTERISK_REGEX.test(bsb)) {
        errors.bank_details = {
          ...errors.bank_details,
          bsb: { type: "pattern", message: "BSB must be a valid number" },
        };
      } else if (!BSB_REGEX.test(bsb)) {
        errors.bank_details = {
          ...errors.bank_details,
          bsb: { type: "pattern", message: "BSB must be exactly 6 digits" },
        };
      }

      if (!account_number) {
        errors.bank_details = {
          ...errors.bank_details,
          account_number: { type: "required", message: "Account number is required" },
        };
      } else if (!NO_ASTERISK_REGEX.test(account_number)) {
        errors.bank_details = {
          ...errors.bank_details,
          account_number: { type: "pattern", message: "Account number must be a valid number" },
        };
      }
    }
  }

  if (bank_type === BankType.OVERSEAS) {
    const { account_name, swift, iban, branch_address, intermediary_swift, intermediary_bank_name } = data.bank_details;
    const hasOverseasFieldsFilled = !!(account_name || swift || iban || branch_address);

    if (hasOverseasFieldsFilled) {
      if (!account_name) {
        errors.bank_details = {
          ...errors.bank_details,
          account_name: { type: "required", message: "Account name is required" },
        };
      } else if (!NO_ASTERISK_REGEX.test(account_name)) {
        errors.bank_details = {
          ...errors.bank_details,
          account_name: { type: "pattern", message: "Account name is not valid" },
        };
      }

      if (!swift) {
        errors.bank_details = {
          ...errors.bank_details,
          swift: { type: "required", message: "SWIFT BIC code is required" },
        };
      } else if (!NO_ASTERISK_REGEX.test(swift)) {
        errors.bank_details = {
          ...errors.bank_details,
          swift: { type: "pattern", message: "SWIFT BIC code is not valid" },
        };
      }

      if (!iban) {
        errors.bank_details = {
          ...errors.bank_details,
          iban: { type: "required", message: "IBAN is required" },
        };
      } else if (!NO_ASTERISK_REGEX.test(iban)) {
        errors.bank_details = {
          ...errors.bank_details,
          iban: { type: "pattern", message: "IBAN is not valid" },
        };
      }
      if (!branch_address) {
        errors.bank_details = {
          ...errors.bank_details,
          branch_address: { type: "required", message: "Overseas country is required" },
        };
      }
    }

    if (intermediary_bank_name && !NO_ASTERISK_REGEX.test(intermediary_bank_name)) {
      errors.bank_details = {
        ...errors.bank_details,
        intermediary_bank_name: { type: "pattern", message: "Intermediary bank name is not valid" },
      };
    }

    if (intermediary_swift && !NO_ASTERISK_REGEX.test(intermediary_swift)) {
      errors.bank_details = {
        ...errors.bank_details,
        intermediary_swift: { type: "pattern", message: "Intermediary SWIFT code is not valid" },
      };
    }
  }

  return {
    values: data,
    errors,
  };
};
